import { useAppDispatch, useAppSelector } from "../../../state/redux/hooks";
import {
  selectViewingRecord,
  setViewingRecord,
} from "../../../state/redux/slices/ui";
import { Button } from "../button";
import {
  SquareArrowOutUpRight,
  X,
  FileText,
  FileCode,
  Edit,
} from "lucide-react";
import { useNavigate } from "react-router";

import { ExternalLink } from "lucide-react";
import RecordView from "./RecordView";
import RecordSheet from "./RecordSheet";
import { useState } from "react";
import RecordIntervalName from "./RecordIntervalName";
import { useCanEditRecord, useFullRecord } from "@/api/hooks";

export function ExternalUrl({ href, label }: { href: string; label?: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
    >
      <span>{label ?? href}</span>
      <ExternalLink className="h-4 w-4" aria-hidden="true" />
    </a>
  );
}

export default function RecordPreview() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const listRecord = useAppSelector(selectViewingRecord);
  const record = useFullRecord(listRecord);
  // The excerpt is fine to *read* while the full text is on its way, but not to
  // edit: the form seeds from this record, so saving from it would write the
  // excerpt back over the record. `content_truncated` clears when the fetch
  // lands, and the button appears then.
  const canEdit = useCanEditRecord(record) && !record?.content_truncated;
  const [markdownEnabled, setMarkdownEnabled] = useState(true);

  function hasMarkdown(content: string | null | undefined) {
    if (!content) return false;
    const mdRegex =
      /(^|\n)(#{1,6}\s)|(`{1,3})|(\*\*|__)|(^|\n)([-*+]\s|\d+\.)|(\[.+?\]\(.+?\))|(^|\n)>/m;
    return mdRegex.test(content);
  }
  const contentHasMarkdown = hasMarkdown(record?.content);

  const handleClose = () => dispatch(setViewingRecord(null));

  if (!record) return null;

  return (
    <div className="relative w-full h-full flex flex-col">
      {/* Fixed header: title + date + rule */}
      <div className="px-[38px] pt-[30px] pb-0 flex-shrink-0">
        <div className="flex justify-between items-start gap-4 pr-8">
          <h1
            className="text-[25px] font-semibold leading-[1.18] text-foreground"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            {record.title || "No title"}
          </h1>
        </div>
        {"interval" in record && (
          <div className="font-mono text-[12px] text-muted-foreground mt-[11px] tracking-[.01em]">
            <RecordIntervalName record={record} />
          </div>
        )}
        <div className="w-[40px] h-[2px] bg-primary mt-[22px] mb-[22px]" />
      </div>
      {/* Scrollable body: only RecordView content scrolls */}
      <div className="grow overflow-auto px-[38px] pb-6">
        <RecordView record={record} markdownEnabled={markdownEnabled} />
      </div>
      {/* Footer toolbar */}
      <div className="flex-shrink-0 border-t border-border px-[26px] py-[13px] flex items-center justify-between">
        <div>
          {contentHasMarkdown && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMarkdownEnabled(!markdownEnabled)}
              className="gap-1 text-muted-foreground hover:text-foreground"
            >
              {markdownEnabled ? (
                <FileText className="size-4" />
              ) : (
                <FileCode className="size-4" />
              )}
              {markdownEnabled ? "Raw" : "Markdown"}
            </Button>
          )}
        </div>
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/record/${record.id}`)}
            className="w-[30px] h-[30px] rounded-[6px] text-muted-foreground hover:bg-row-hover hover:text-foreground"
          >
            <SquareArrowOutUpRight className="size-[15px]" />
          </Button>
          {canEdit && (
            <RecordSheet record={record} title="Edit record">
              <Button
                variant="ghost"
                size="icon"
                className="w-[30px] h-[30px] rounded-[6px] text-muted-foreground hover:bg-row-hover hover:text-foreground"
              >
                <Edit className="size-[15px]" />
              </Button>
            </RecordSheet>
          )}
        </div>
      </div>
      {/* Close button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleClose}
        className="absolute top-[28px] right-[26px] w-[28px] h-[28px] rounded-[6px] text-muted-foreground hover:bg-row-hover hover:text-foreground"
      >
        <X className="size-[14px]" />
      </Button>
    </div>
  );
}
